import type { ServiceBudgetRequest, ServiceUsage, SetServiceBudget, SvcService } from "@/lib/api";

/** Every services gateway service, in the order the admin pages show them. */
export const SVC_SERVICES: SvcService[] = ["llm", "serper", "langfuse"];

/** What each services gateway service is called in the UI. */
export const SERVICE_LABEL: Record<SvcService, string> = {
  serper: "Web search (Serper)",
  langfuse: "Tracing (Langfuse)",
  llm: "Models (LLM)",
};

/** A budget request's status, as its requester reads it. */
export const REQUEST_STATUS: Record<ServiceBudgetRequest["status"], string> = {
  pending: "Waiting for an admin",
  approved: "Approved",
  rejected: "Rejected",
  failed: "Failed",
};

/** How long a rotated-out key keeps working, as an admin can choose it. */
export const OVERLAP_HOURS = [0, 1, 6, 24, 48];

/** Where a suspension in force was made. */
export type SuspensionScope = NonNullable<ServiceUsage["suspended_scope"]>;

/** Where a suspension was made, as an admin reads it. */
export const SUSPENSION_SCOPE: Record<SuspensionScope, string> = {
  bucket: "this service only",
  project: "the whole project",
  service: "every project (kill switch)",
};

/** A suspension's reason: what the backend and the gateway's registry accept. */
export const REASON_MIN = 10;
export const REASON_MAX = 500;
// The backend's rule (`SetSvcSuspension`): no controls, invisible format
// characters (bidi overrides, zero-width), line separators, private-use,
// unassigned or surrogate code points, letters that render blank, or piles of
// combining marks -- the owner would read something other than what was typed.
const HIDDEN_CHARACTERS =
  /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}\p{Co}\p{Cn}\p{Cs}\u115F\u1160\u3164\uFFA0\u2800]/u;
const PILED_MARKS = /[\p{Mn}\p{Me}]{4,}/u;

type Unit = ServiceUsage["unit"];

const USD_AMOUNT = /^\d{1,7}(\.\d{1,2})?$/;
const REQUEST_AMOUNT = /^\d{1,9}$/;

/** The unit a service's budget counts. */
export function unitOf(service: SvcService): Unit {
  return service === "llm" ? "usd" : "requests";
}

/** `$12.50` or `5,000 requests`. */
export function formatAmount(unit: Unit, value: number): string {
  return unit === "usd"
    ? `$${value.toFixed(2)}`
    : `${value.toLocaleString("en-GB")} request${value === 1 ? "" : "s"}`;
}

/** A typed amount as the API takes it, or `null` while it is not a valid one. */
export function parseBudget(unit: Unit, text: string): SetServiceBudget | null {
  const trimmed = text.trim();
  if (unit === "usd") {
    return USD_AMOUNT.test(trimmed) ? { monthly_usd: trimmed } : null;
  }
  return REQUEST_AMOUNT.test(trimmed) ? { monthly_requests: Number(trimmed) } : null;
}

/** The budget that puts a service back on the environment default. */
export function defaultBudget(unit: Unit): SetServiceBudget {
  return unit === "usd" ? { monthly_usd: null } : { monthly_requests: null };
}

/** The budget set for this project, or `null` for the environment default. */
export function budgetOf(usage: ServiceUsage): number | null {
  return (usage.unit === "usd" ? usage.monthly_usd : usage.monthly_requests) ?? null;
}

/** What a budget request asks for, a month. */
export function requestedAmount(request: ServiceBudgetRequest): string {
  const unit = unitOf(request.service);
  const value = (unit === "usd" ? request.monthly_usd : request.monthly_requests) ?? 0;
  return `${formatAmount(unit, value)} a month`;
}

/** The budget the project had when it asked. */
export function currentAmount(request: ServiceBudgetRequest): string {
  const unit = unitOf(request.service);
  const value = unit === "usd" ? request.current_monthly_usd : request.current_monthly_requests;
  return value != null ? formatAmount(unit, value) : "the environment default";
}

/** What an admin granted, or `null` when nothing (yet). */
export function grantedAmount(request: ServiceBudgetRequest): string | null {
  const unit = unitOf(request.service);
  const value = unit === "usd" ? request.granted_monthly_usd : request.granted_monthly_requests;
  return value != null ? formatAmount(unit, value) : null;
}

/** A typed reason as it is sent: line breaks and runs of spaces become one space. */
export function normalizeReason(text: string): string {
  return text.replace(/\s+/gu, " ").trim();
}

/** Why a (normalized) reason cannot be sent, or `null` when it can. */
export function reasonProblem(reason: string): string | null {
  // Characters as the backend counts them (code points, not UTF-16 units).
  const length = [...reason].length;
  if (length < REASON_MIN) {
    return `At least ${REASON_MIN} characters: the project's owners read it.`;
  }
  if (length > REASON_MAX) {
    return `At most ${REASON_MAX} characters.`;
  }
  if (HIDDEN_CHARACTERS.test(reason) || PILED_MARKS.test(reason)) {
    return "No control, invisible or blank characters.";
  }
  return null;
}

/** A service's label inside a sentence: "models (LLM)", "web search (Serper)". */
export function serviceNoun(service: SvcService): string {
  const label = SERVICE_LABEL[service];
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** Services in words: "models (LLM) and tracing (Langfuse)". */
export function serviceList(services: SvcService[]): string {
  return services.map(serviceNoun).join(" and ");
}
